<script lang="ts" setup>
import type { SizeVariants } from '~kui/types/kui';
import type { EmblaOptionsType } from 'embla-carousel';

import { useResizeObserver } from '@vueuse/core';
import { Icon } from '~ds/components/ui/icon';
import { ICONS } from '~ds/lib/icons';
import { useCarousel } from '~kui/composables/useCarousel';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import { useI18n } from 'vue-i18n';

interface Props {
  gap?: Extract<SizeVariants, 'sm' | 'md' | 'lg'>;
  showArrows?: boolean;
  showDots?: boolean;
  showFade?: boolean;
  ariaLabel?: string;
  containerClassName?: string;
  snapToItem?: boolean;
  /**
   * Where the arrows sit vertically: `center` is the row's middle; `media` levels them with the
   * first card's photo (a DS content card's `card-media`), for cards with a title and meta below.
   */
  arrowAlign?: 'center' | 'media';
  /**
   * Keeps the arrows showing under a coarse pointer, where there is no hover to reveal them, as a
   * single-tap alternative to swiping the row. Off by default: on a phone the discs sit over the
   * edge cards.
   */
  arrowsOnTouch?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  gap: 'md',
  showArrows: true,
  showDots: false,
  showFade: true,
  ariaLabel: undefined,
  containerClassName: '',
  snapToItem: false,
  arrowAlign: 'center',
  arrowsOnTouch: false,
});

const emit = defineEmits<{
  scrollEndReached: [];
}>();

const { t } = useI18n();
const resolvedAriaLabel = computed(() => props.ariaLabel ?? t('unified.common.scrollableList'));

// snapToItem trades momentum scrolling for one slide per gesture: dragFree off makes every drag
// settle on a snap point, and skipSnaps off stops a fast flick from flying past the next one.
const emblaOptions = computed<EmblaOptionsType>(() => ({
  dragFree: !props.snapToItem,
  skipSnaps: false,
  containScroll: 'trimSnaps',
  align: 'start',
  loop: false,
  duration: 25,
}));

const emblaPlugins = computed(() => [WheelGesturesPlugin({ forceWheelAxis: 'x' })]);

const {
  emblaRef,
  emblaApi,
  selectedIndex,
  scrollSnaps,
  canScrollPrev,
  canScrollNext,
  slides,
  scrollPrev,
  scrollNext,
  scrollTo,
} = useCarousel({ options: emblaOptions, plugins: emblaPlugins });

const hasOverflow = computed(() => canScrollPrev.value || canScrollNext.value);

const MEDIA_SELECTOR = '[role="listitem"] [data-slot="card-media"]';
const rootRef = ref<HTMLElement | null>(null);
const arrowTop = ref<number | null>(null);
const measuredRoot = computed(() => (props.arrowAlign === 'media' ? rootRef.value : null));

const placeArrows = () => {
  const root = measuredRoot.value;
  const media = root?.querySelector<HTMLElement>(MEDIA_SELECTOR);
  if (!root || !media) {
    arrowTop.value = null;
    return;
  }
  const rootBox = root.getBoundingClientRect();
  const mediaBox = media.getBoundingClientRect();
  arrowTop.value = mediaBox.height > 0 ? mediaBox.top - rootBox.top + mediaBox.height / 2 : null;
};

const arrowStyle = computed(() => (arrowTop.value === null ? undefined : { top: `${arrowTop.value}px` }));

useResizeObserver(measuredRoot, placeArrows);
watch([slides, measuredRoot], () => nextTick(placeArrows));

const gapClasses = {
  sm: 'gap-3',
  md: 'gap-4',
  lg: 'gap-6',
};

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'ArrowRight') {
    event.preventDefault();
    scrollNext();
  }
  else if (event.key === 'ArrowLeft') {
    event.preventDefault();
    scrollPrev();
  }
};

watch(canScrollNext, (next, prev) => {
  if (prev && !next) {
    emit('scrollEndReached');
  }
});

// Fires when the row has no overflow at all — there's nothing to scroll, so the
// "end" is reached the moment Embla settles. Without this, consumers gating on
// scrollEndReached (e.g. Continue buttons on short lists) stay disabled forever.
const emitIfNoOverflow = () => {
  if (!canScrollPrev.value && !canScrollNext.value) {
    emit('scrollEndReached');
  }
};

onMounted(() => {
  emblaApi.value?.on('init', emitIfNoOverflow);
  emblaApi.value?.on('reInit', emitIfNoOverflow);
});

onUnmounted(() => {
  emblaApi.value?.off('init', emitIfNoOverflow);
  emblaApi.value?.off('reInit', emitIfNoOverflow);
});
</script>

<template>
  <div
    ref="rootRef"
    class="relative group/scroll"
    role="region"
    :aria-label="resolvedAriaLabel"
    :aria-roledescription="t('unified.common.scrollableListRoleDescription')"
  >
    <!-- Embla viewport -->
    <div
      ref="emblaRef"
      tabindex="0"
      class="overflow-hidden cursor-grab active:cursor-grabbing focus-visible:outline focus-visible:outline-offset-2"
      :class="[
        containerClassName,
      ]"
      @keydown="handleKeydown"
    >
      <div
        class="flex p-2"
        :class="[gapClasses[gap]]"
        role="list"
      >
        <div
          v-for="(slide, index) in slides"
          :key="index"
          role="listitem"
          class="shrink-0"
        >
          <component :is="slide" />
        </div>
      </div>
    </div>
    <div
      v-if="showFade && canScrollPrev"
      data-testid="scrollable-row-fade-start"
      aria-hidden="true"
      class="pointer-events-none absolute inset-y-0 left-0 z-10 w-6 md:w-16 bg-linear-to-r from-white/80 to-transparent"
    />
    <div
      v-if="showFade && canScrollNext"
      data-testid="scrollable-row-fade-end"
      aria-hidden="true"
      class="pointer-events-none absolute inset-y-0 right-0 z-10 w-6 md:w-16 bg-linear-to-l from-white/80 to-transparent"
    />

    <button
      v-if="showArrows && hasOverflow"
      :hidden="!canScrollPrev || undefined"
      type="button"
      tabindex="-1"
      :aria-label="$t('unified.common.scrollLeft')"
      :style="arrowStyle"
      class="absolute left-1 top-1/2 z-20 -translate-y-1/2 flex items-center justify-center rounded-full bg-white shadow-md transition-all hover:shadow-lg size-8 opacity-0 pointer-events-none group-hover/scroll:opacity-100 group-hover/scroll:pointer-events-auto group-has-focus-visible/scroll:opacity-100 group-has-focus-visible/scroll:pointer-events-auto"
      :class="{ 'pointer-coarse:opacity-100 pointer-coarse:pointer-events-auto': arrowsOnTouch, 'opacity-0! pointer-events-none!': !canScrollPrev }"
      @click="scrollPrev"
    >
      <Icon :name="ICONS.arrowLeft" size="sm" />
    </button>

    <button
      v-if="showArrows && hasOverflow"
      :hidden="!canScrollNext || undefined"
      type="button"
      tabindex="-1"
      :aria-label="$t('unified.common.scrollRight')"
      :style="arrowStyle"
      class="absolute right-1 top-1/2 z-20 -translate-y-1/2 flex items-center justify-center rounded-full bg-white shadow-md transition-all hover:shadow-lg size-8 opacity-0 pointer-events-none group-hover/scroll:opacity-100 group-hover/scroll:pointer-events-auto group-has-focus-visible/scroll:opacity-100 group-has-focus-visible/scroll:pointer-events-auto"
      :class="{ 'pointer-coarse:opacity-100 pointer-coarse:pointer-events-auto': arrowsOnTouch, 'opacity-0! pointer-events-none!': !canScrollNext }"
      @click="scrollNext"
    >
      <Icon :name="ICONS.arrowRight" size="sm" />
    </button>

    <KUICoreCarouselDots
      v-if="showDots && scrollSnaps.length > 1"
      class="mt-4"
      :total="scrollSnaps.length"
      :active-index="selectedIndex"
      size="sm"
      @select="scrollTo"
    />
  </div>
</template>
